import { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/hooks/useCart";
import { useWishlist } from "@/hooks/useWishlist";
import { useAuth } from "@/hooks/useAuth";
import { useSiteSettings, useCategories } from "@/hooks/useApi";

export default function Header() {
  const { count, toggleDrawer } = useCart();
  const { wishlistCount } = useWishlist();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { data: settings } = useSiteSettings();
  const { data: apiCategories } = useCategories();

  const shippingMsg =
    settings?.shipping?.message || "شحن مجاني للطلبات فوق 300 شيكل";

  const categories = Array.isArray(apiCategories)
    ? apiCategories.filter((c) => c.active && !c.parent_id)
    : [];
  const navLinks = [
    { name: "الرئيسية", href: "/" },
    { name: "المتجر", href: "/shop" },
    ...categories.map((c) => ({
      name: c.name,
      href: `/shop?category=${encodeURIComponent(c.name)}`,
    })),
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/shop?search=${encodeURIComponent(searchQuery.trim())}`;
    }
  };

  return (
    <>
      <div className="text-[#E2F0BF] bg-[#79A286] text-center py-2.5 px-4 text-xs md:text-sm font-medium">
        <span className="inline-flex items-center gap-2">
          <i className="ri-truck-line" />
          {shippingMsg}
        </span>
      </div>

      <header className="sticky top-0 z-50 bg-cream-50/95 backdrop-blur-md border-b border-cream-300">
        <div className="container-main">
          <div className="flex items-center justify-between h-16 md:h-20">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center"
            >
              <i
                className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl text-charcoal-900`}
              />
            </button>

            <Link to="/" className="flex-shrink-0">
              <img
                src="/logo.svg"
                alt="سعاد الحاج"
                className="h-8 md:h-10 w-auto"
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-sm font-medium text-charcoal-600 hover:text-charcoal-900 transition-colors whitespace-nowrap"
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-2 md:gap-4">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="w-10 h-10 flex items-center justify-center text-charcoal-600 hover:text-charcoal-900"
              >
                <i className="ri-search-line text-lg" />
              </button>
              <Link
                to={user ? "/account" : "/login"}
                className="hidden md:flex w-10 h-10 items-center justify-center text-charcoal-600 hover:text-charcoal-900 relative"
              >
                <i
                  className={`${user ? "ri-user-fill" : "ri-user-line"} text-lg`}
                />
              </Link>
              <Link
                to="/wishlist"
                className="hidden md:flex w-10 h-10 items-center justify-center text-charcoal-600 hover:text-charcoal-900 relative"
              >
                <i className="ri-heart-line text-lg" />
                {wishlistCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-blush-200 text-charcoal-900 text-[10px] font-bold rounded-full flex items-center justify-center">
                    {wishlistCount}
                  </span>
                )}
              </Link>
              <button
                onClick={toggleDrawer}
                className="w-10 h-10 flex items-center justify-center text-charcoal-600 hover:text-charcoal-900 relative"
              >
                <i className="ri-shopping-bag-line text-lg" />
                {count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-charcoal-900 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {count}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-cream-300 bg-cream-50">
            <div className="container-main py-4">
              <form onSubmit={handleSearch} className="relative max-w-xl">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="ابحثي عن منتج..."
                  className="w-full bg-white border border-cream-300 rounded-xl px-5 py-3 pr-12 text-sm text-charcoal-900 placeholder:text-charcoal-400 focus:outline-none focus:border-blush-200"
                />
                <button
                  type="submit"
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                >
                  <i className="ri-search-line text-charcoal-400" />
                </button>
              </form>
            </div>
          </div>
        )}

        {mobileOpen && (
          <div className="md:hidden bg-cream-50 border-t border-cream-300">
            <nav className="container-main py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block py-3 px-4 text-base font-medium text-charcoal-600 hover:bg-cream-200 rounded-lg transition-colors"
                >
                  {link.name}
                </Link>
              ))}
              <div className="border-t border-cream-300 pt-2 mt-2">
                {user ? (
                  <Link
                    to="/account"
                    onClick={() => setMobileOpen(false)}
                    className="block py-3 px-4 text-base font-medium text-charcoal-600 hover:bg-cream-200 rounded-lg transition-colors"
                  >
                    <i className="ri-user-fill ml-2" />
                    حسابي
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    className="block py-3 px-4 text-base font-medium text-charcoal-600 hover:bg-cream-200 rounded-lg transition-colors"
                  >
                    <i className="ri-user-line ml-2" />
                    تسجيل الدخول
                  </Link>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
